import React, { useEffect, useRef, useState, useCallback } from 'react'
// import MapService, { MapServiceFacade } from '@/services/arcgis/MapServiceFacade'
import { MapServiceFacade } from '@/services/arcgis/MapServiceFacade'
import type { WardInfo } from '@/types/ward'
import { LoadingDialog } from '@/components/common/LoadingDialog'
import { MapComparisonSync } from './MapComparisonSync'
import { MapControls } from './MapControls'
import { ViewModeToggle } from './ViewModeToggle'
import { PlaceInfoPopup } from './PlaceInfoPopup'
import { VI_MESSAGES } from '@/constants/vietnamese'
import { useWardStore } from '@/stores/wardStore'
import { useUIStore } from '@/stores/uiStore'
// import { useComparisonStore } from '@/stores/comparisonStore'
import { mapInstanceManager } from '@/services/arcgis/MapInstanceManager'
import '@arcgis/map-components/dist/components/arcgis-map'
import '@arcgis/map-components/dist/components/arcgis-scene'
import { cn } from '@/lib/utils'
// import type WebScene from '@arcgis/core/WebScene'
// import type WebMap from '@arcgis/core/WebMap'
// import WebMap from '@arcgis/core/WebMap'
// import WebScene from '@arcgis/core/WebScene'

interface MapViewProps {
  onWardSelect?: (ward: WardInfo) => void
  selectedWardId?: number
  className?: string
}

/**
 * Component hiển thị bản đồ với ArcGIS
 */
export const MapView: React.FC<MapViewProps> = ({
  onWardSelect,
  selectedWardId,
  className = '',
}) => {
  // const mapRef = useRef<HTMLDivElement>(null)
  const mapServiceRef = useRef<MapServiceFacade | null>(null)
  const onWardSelectRef = useRef(onWardSelect)
  const [error, setError] = useState<string | null>(null)
  const hoveredWardId = useWardStore((state) => state.hoveredWardId)
  const { setMapLoading, viewMode } = useUIStore()
  // const { isComparing, addWard, removeWard, isWardSelected, canAddMore } = useComparisonStore()
  const [showLoadingDialog, setShowLoadingDialog] = useState(false)
  const [locationEnabled, setLocationEnabled] = useState(false)
  const loadingStartTimeRef = useRef<number>(0)
  const [placePopup, setPlacePopup] = useState<{
    feature: __esri.Graphic
    screenPoint: { x: number; y: number }
  } | null>(null)

  // Get map instance from singleton manager - memoized to prevent unnecessary re-renders
  // const map = useMemo(() => {
  //   return mapInstanceManager.getMapInstance(viewMode)
  // }, [viewMode])

  // Update ref when onWardSelect changes
  useEffect(() => {
    onWardSelectRef.current = onWardSelect
  }, [onWardSelect])

  // Khởi tạo map service
  useEffect(() => {
    //   if (!mapRef.current) return
    //
    const initializeMap = async () => {
      try {
        // Start loading with minimum display time
        loadingStartTimeRef.current = Date.now()
        setMapLoading(true)
        setShowLoadingDialog(true)
        setError(null)

        // Lấy config từ environment variables
        // const webMapId = import.meta.env.VITE_WEBMAP_ID
        // const portalUrl = import.meta.env.VITE_PORTAL_URL
        // const featureServiceUrl = import.meta.env.VITE_FEATURE_SERVICE_URL
        //
        // // Kiểm tra cấu hình - ưu tiên WebMap
        // if (!webMapId || !portalUrl) {
        //   // Fallback: Kiểm tra feature service trực tiếp
        //   if (!featureServiceUrl || featureServiceUrl.includes('YOUR_ORG')) {
        //     throw new Error('Không tải được bản đồ.')
        //   }
        // }
        //
        // // Sử dụng singleton MapService
        // const mapService = await MapService.initialize(mapRef.current!)
        // mapServiceRef.current = mapService
        //
        // // Xử lý sự kiện click với logging đầy đủ
        // mapService.onMapClick(async (clickEvent) => {
        //   console.log('[MapView] Map click event received:', {
        //     clickType: clickEvent.clickType,
        //     hasFeatures: !!clickEvent.features?.length,
        //     hasWardFeature: !!clickEvent.wardFeature,
        //   })
        //
        //   // Handle place click - show popup
        //   if (
        //     clickEvent.clickType === 'place' &&
        //     clickEvent.features &&
        //     clickEvent.features.length > 0
        //   ) {
        //     console.log('[MapView] Showing place popup')
        //     const placeFeature = clickEvent.features[0]
        //     setPlacePopup({
        //       feature: placeFeature,
        //       screenPoint: clickEvent.screenPoint,
        //     })
        //     // Highlight the place
        //     await mapService.highlightPlace(placeFeature)
        //   }
        //   // Handle ward selection if a ward was clicked
        //   else if (clickEvent.wardFeature && clickEvent.wardFeature.attributes) {
        //     const ward = clickEvent.wardFeature.attributes as WardInfo
        //     if (isComparing) {
        //       // In comparison mode
        //       if (isWardSelected(ward.objectid)) {
        //         removeWard(ward.objectid)
        //       } else if (canAddMore()) {
        //         addWard(ward)
        //       }
        //     } else if (onWardSelectRef.current) {
        //       // Normal selection mode
        //       onWardSelectRef.current(ward)
        //     }
        //   }
        // })

        // Ensure minimum loading time of 1.5 seconds
        const elapsedTime = Date.now() - loadingStartTimeRef.current
        const remainingTime = Math.max(0, 1500 - elapsedTime)

        setTimeout(() => {
          setMapLoading(false)
          setShowLoadingDialog(false)
        }, remainingTime)
      } catch (error) {
        console.error('Lỗi khởi tạo bản đồ:', error)
        const errorMessage =
          error instanceof Error ? error.message : 'Không thể tải bản đồ. Vui lòng thử lại sau.'
        setError(errorMessage)
        setMapLoading(false)
        setShowLoadingDialog(false)
      }
    }
    //
    initializeMap()

    // Cleanup
    return () => {
      if (mapServiceRef.current) {
        mapServiceRef.current.destroy()
        mapServiceRef.current = null
      }
    }
  }, [setMapLoading])

  // Xử lý khi phường được chọn thay đổi
  useEffect(() => {
    if (mapServiceRef.current) {
      if (selectedWardId) {
        // mapServiceRef.current.zoomToWard(selectedWardId)
        // TODO: fix this when a ward card is selected
        // mapServiceRef.current.highlightWard(selectedWardId);
      } else {
        // Clear highlight when no ward is selected
        // mapServiceRef.current.highlightWard(null);
      }
    }
  }, [selectedWardId])

  // Xử lý khi hover phường
  useEffect(() => {
    if (hoveredWardId !== null && mapServiceRef.current) {
      // Add hover effect
      // TODO: implement this
      // mapServiceRef.current.highlightWard(hoveredWardId);
    }
  }, [hoveredWardId])

  // Map control handlers
  const handleZoomIn = useCallback(() => {
    // const view = mapServiceRef.current?.getView()
    // if (view) {
    //   view.zoom = Math.min(view.zoom + 1, 20)
    // }
  }, [])

  const handleZoomOut = useCallback(() => {
    // const view = mapServiceRef.current?.getView()
    // if (view) {
    //   view.zoom = Math.max(view.zoom - 1, 0)
    // }
  }, [])

  const handleHome = useCallback(() => {
    // const view = mapServiceRef.current?.getView()
    // if (view) {
    //   view.goTo({
    //     center: [105.804817, 21.028511], // Hanoi center
    //     zoom: 11,
    //   })
    // }
  }, [])

  const handleLocation = useCallback(async () => {
    try {
      if (!locationEnabled) {
        // await MapService.enableLocationTracking()
        // await MapService.goToUserLocation()
        setLocationEnabled(true)
      } else {
        // MapService.disableLocationTracking()
        setLocationEnabled(false)
      }
    } catch (error) {
      console.error('Location error:', error)
      alert(VI_MESSAGES.DIRECTIONS.LOCATION_ERROR)
    }
  }, [locationEnabled])

  return (
    <div className={`relative h-full w-full ${className}`}>
      {/* Map comparison sync */}
      <MapComparisonSync />

      {/* Loading dialog */}
      <LoadingDialog
        open={showLoadingDialog}
        title="Đang tải bản đồ"
        description="Đang khởi tạo bản đồ Hà Nội. Vui lòng chờ trong giây lát..."
      />

      {/* Error message */}
      {error && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white p-4">
          <div className="max-w-2xl text-center">
            <p className="mb-4 whitespace-pre-line text-red-600">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
            >
              {VI_MESSAGES.COMMON.RELOAD}
            </button>
          </div>
        </div>
      )}

      {/* Map container */}
      {/* {showLoadingDialog ? ( */}
      {/*   <MapSkeleton /> */}
      {/* ) : ( */}
      {/*   <div ref={mapRef} className="h-full w-full" /> */}
      {/* )} */}
      {/* <div ref={mapRef} className="h-full w-full" /> */}
      {
        <>
          <div
            className={cn(
              'relative h-full w-full',
              viewMode === '2D' ? 'visible z-[1]' : 'z-[-1] hidden'
            )}
          >
            <arcgis-map
              zoom={11}
              center={[105.804817, 21.028511]}
              map={mapInstanceManager.getMapInstance('2D')}
            ></arcgis-map>
          </div>
          <div
            className={cn(
              'relative h-full w-full',
              viewMode === '3D' ? 'visible z-[1]' : 'z-[-1] hidden'
            )}
          >
            <arcgis-scene
              zoom={11}
              center={[105.804817, 21.028511]}
              map={mapInstanceManager.getMapInstance('3D')}
            ></arcgis-scene>
          </div>
        </>
      }

      {/* Map controls */}
      {!error && (
        <>
          <MapControls
            className="absolute top-4 right-4 z-[2]"
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onHome={handleHome}
            onLocation={handleLocation}
            locationEnabled={locationEnabled}
          />
          {/* View mode toggle */}
          <ViewModeToggle className="absolute top-4 right-20 z-[2]" />
        </>
      )}

      {/* Place popup */}
      {placePopup && (
        <PlaceInfoPopup
          feature={placePopup.feature}
          screenPoint={placePopup.screenPoint}
          onClose={() => {
            setPlacePopup(null)
            // Clear place highlight when closing popup
            // MapService.clearPlaceHighlight()
          }}
        />
      )}
    </div>
  )
}
